import './features/partner-coverage/captureCoverageHandoff';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { defaultQueryClientConfig } from './api/hooks';
import { AuthProvider } from './contexts/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import './setupHelmet';
import './index.css';
import CoveragePublicApp from './features/partner-coverage/CoveragePublicApp';
import { captureConnectionInvitation } from './features/partner-coverage/connectionInvitation';

// These styles are shared across lazy-loaded routes. Loading them from the
// entry point gives Webpack one deterministic order for the extracted CSS.
import './components/GroupIntake/form-primitives.css';
import './components/GroupIntake/components/DualRangeSlider.css';
import './components/Company/FacilityCredentialingConfig.css';
import './components/billing/PricingTierSelection.css';
import './components/Profile/ProfileActionBar.css';
import './components/Notifications/NotificationBell.css';
import './pages/SettingsPageCard.css';
import './components/Settings/CredentialVerificationDisplay.css';
import './components/Settings/NotificationSettings.css';
import './components/Settings/ReviewStatusCard.css';
import './components/GroupUserManagement/InviteUserModal.css';
import './components/GroupUserManagement/GroupUsersList.css';
import './components/AudienceExclusions/AudienceExclusionsSelector.css';
import './components/billing/SubscriptionManagement.css';

const initializeViewportUnits = () => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const setAppHeight = () => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    if (viewportHeight) {
      document.documentElement.style.setProperty('--app-viewport-height', `${viewportHeight}px`);
    }
  };

  setAppHeight();

  const handleViewportResize = () => setAppHeight();
  const visualViewport = window.visualViewport;
  visualViewport?.addEventListener('resize', handleViewportResize);
  visualViewport?.addEventListener('scroll', handleViewportResize);
  window.addEventListener('orientationchange', setAppHeight);
  window.addEventListener('resize', setAppHeight);
  window.addEventListener('pageshow', setAppHeight);

  const nudgeScroll = () => {
    window.requestAnimationFrame(() => window.scrollTo(0, 1));
    window.removeEventListener('touchend', nudgeScroll);
  };
  window.addEventListener('touchend', nudgeScroll, { passive: true });

  return () => {
    visualViewport?.removeEventListener('resize', handleViewportResize);
    visualViewport?.removeEventListener('scroll', handleViewportResize);
    window.removeEventListener('orientationchange', setAppHeight);
    window.removeEventListener('resize', setAppHeight);
    window.removeEventListener('pageshow', setAppHeight);
    window.removeEventListener('touchend', nudgeScroll);
  };
};

const teardownViewportUnits = initializeViewportUnits();

if (module.hot) {
  module.hot.dispose(() => {
    teardownViewportUnits();
  });
}

const container = document.getElementById('root');
const root = createRoot(container);
captureConnectionInvitation();
const isCoveragePublic = /^\/partners\/(setup|demo)\/[^/]+\/?$/.test(window.location.pathname);

const queryClient = new QueryClient(defaultQueryClientConfig);

root.render(
  isCoveragePublic ? <CoveragePublicApp /> : <React.StrictMode>
    <HelmetProvider>
      <AuthProvider>
          <QueryClientProvider client={queryClient}>
        <App />
          </QueryClientProvider>
      </AuthProvider>
    </HelmetProvider>
  </React.StrictMode>
);
